import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { useToast } from "#/hooks/ToastContext";
import { qk } from "#/lib/queryKeys";
import {
  getMedicalConceptById,
  listExistingRelatedMedicalConcepts,
  listMedicalConceptArticles,
  listMedicalConceptOutlines,
  RelatedConceptSuggestion,
} from "#/services/articleOutlines";
import useMedicalConceptDetailMutations from "#/admin/medical-concepts/hooks/useMedicalConceptDetailMutations";

function relatedSuggestionKey(item: RelatedConceptSuggestion, index: number) {
  return [
    item.name,
    item.relationship_type,
    item.concept_type,
    item.semantic_type_ids?.join(",") ?? "",
    String(index),
  ].join("::");
}

export function MedicalConceptDetail({ id }: { id: string }) {
  const { showToast } = useToast();
  const [relatedSuggestionLimit, setRelatedSuggestionLimit] =
    useState<number>(5);
  const [relatedSuggestions, setRelatedSuggestions] = useState<
    RelatedConceptSuggestion[]
  >([]);
  const [isGeneratingAndApplying, setIsGeneratingAndApplying] =
    useState(false);
  const [isRegenerateQueued, setIsRegenerateQueued] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<
    "idle" | "suggesting" | "applying" | "refreshing"
  >("idle");

  useEffect(() => {
    setIsRegenerateQueued(false);
  }, [id]);

  const {
    triggerOutlineFromConcept,
    regenerateOutline,
    suggestRelated,
    materializeRelated,
  } = useMedicalConceptDetailMutations(id);

  const detailQuery = useQuery({
    queryKey: qk.medicalConceptDetail(id),
    queryFn: () => getMedicalConceptById(id),
    enabled: Boolean(id),
  });

  const relatedQuery = useQuery({
    queryKey: qk.medicalConceptRelated(id),
    queryFn: () => listExistingRelatedMedicalConcepts(id),
    enabled: Boolean(id),
  });

  const outlinesQuery = useQuery({
    queryKey: qk.medicalConceptOutlines(id),
    queryFn: () => listMedicalConceptOutlines(id),
    enabled: Boolean(id),
  });

  const articlesQuery = useQuery({
    queryKey: qk.medicalConceptArticles(id),
    queryFn: () => listMedicalConceptArticles(id),
    enabled: Boolean(id),
  });

  const handleCreateFromConcept = async () => {
    const selected = detailQuery.data;
    if (!selected) {
      showToast("Concept not loaded.", "error");
      return;
    }

    try {
      const stageResult = await triggerOutlineFromConcept.mutateAsync({
        conceptName: selected.name,
      });

      if (stageResult.selected > 0) {
        showToast("Outline trigger queued and generation started.", "success");
      } else {
        showToast(
          "Backlog item created, but outline stage did not select this item yet.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Failed to create backlog item from concept.",
        "error"
      );
    }
  };

  const handleRegenerateOutline = async () => {
    try {
      const data = await regenerateOutline.mutateAsync();
      setIsRegenerateQueued(true);
      showToast(
        `Outline ${data.outlineId.slice(0, 8)} regenerated and queued (${data.status}).`,
        "success"
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to regenerate outline for concept.";

      if (/already queued|in progress/i.test(message)) {
        setIsRegenerateQueued(true);
      }

      showToast(message, "error");
    }
  };

  const retryRefreshDetail = async () => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      await Promise.all([
        detailQuery.refetch(),
        relatedQuery.refetch(),
        outlinesQuery.refetch(),
        articlesQuery.refetch(),
      ]);
      if (attempt < 2) {
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
      }
    }
  };

  const handleGenerateAndApply = async () => {
    try {
      setIsGeneratingAndApplying(true);
      setGenerationStatus("suggesting");

      const data = await suggestRelated.mutateAsync(relatedSuggestionLimit);
      const nextSuggestions = data.suggestions || [];
      setRelatedSuggestions(nextSuggestions);

      if (!nextSuggestions.length) {
        showToast("No new related concepts were suggested.", "success");
        return;
      }

      setGenerationStatus("applying");
      const result = await materializeRelated.mutateAsync(nextSuggestions);

      setGenerationStatus("refreshing");
      await retryRefreshDetail();

      showToast(
        `Generated and applied ${result.results?.length || 0} related concepts.`,
        "success"
      );
    } catch (error) {
      showToast(
        error instanceof Error
          ? error.message
          : "Failed to generate and apply related concepts.",
        "error"
      );
    } finally {
      setGenerationStatus("idle");
      setIsGeneratingAndApplying(false);
    }
  };

  if (detailQuery.isLoading) {
    return (
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Body className="bg-white">
          <Typography variant="body2" className="text-brown-600">
            Loading concept…
          </Typography>
        </Card.Body>
      </Card>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Body className="bg-white">
          <Typography variant="body2" className="text-terracotta">
            {detailQuery.error instanceof Error
              ? detailQuery.error.message
              : "Unable to load concept."}
          </Typography>
        </Card.Body>
      </Card>
    );
  }

  const concept = detailQuery.data;
  const hasExistingArticles = (articlesQuery.data || []).length > 0;
  const isArticlesCheckPending = articlesQuery.isLoading || articlesQuery.isFetching;

  return (
    <div className="flex flex-col gap-6">
      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <div className="flex flex-col gap-1">
            <Typography variant="h4" className="text-brown-900">
              {concept.name}
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Type: {concept.concept_type ?? "Unspecified"}
            </Typography>
          </div>
        </Card.Header>
        <Card.Body className="bg-white space-y-3">
          <Typography variant="body2" className="text-brown-700">
            {concept.description?.trim() || "No description provided."}
          </Typography>

          <div className="flex flex-wrap gap-2">
            <ActionButton
              size="sm"
              onClick={handleCreateFromConcept}
              isLoading={triggerOutlineFromConcept.isPending}
              disabled={
                isArticlesCheckPending ||
                hasExistingArticles ||
                triggerOutlineFromConcept.isPending ||
                regenerateOutline.isPending ||
                isRegenerateQueued
              }
            >
              Add to outline backlog
            </ActionButton>
            <ActionButton
              size="sm"
              tone="secondary"
              onClick={handleRegenerateOutline}
              isLoading={regenerateOutline.isPending}
              disabled={
                isArticlesCheckPending ||
                hasExistingArticles ||
                regenerateOutline.isPending ||
                triggerOutlineFromConcept.isPending ||
                isRegenerateQueued
              }
            >
              Regenerate existing outline
            </ActionButton>
          </div>

          {isRegenerateQueued ? (
            <Typography variant="body2" className="text-brown-600">
              Outline regeneration is queued or in progress for this concept.
            </Typography>
          ) : null}

          {isArticlesCheckPending ? (
            <Typography variant="body2" className="text-brown-600">
              Checking for existing articles before enabling outline actions…
            </Typography>
          ) : hasExistingArticles ? (
            <Typography variant="body2" className="text-emerald-700">
              This concept already has published/generated article(s). Outline generation actions are disabled.
            </Typography>
          ) : null}
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Existing articles
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          {articlesQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading related articles…
            </Typography>
          ) : articlesQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {articlesQuery.error instanceof Error
                ? articlesQuery.error.message
                : "Unable to load related articles."}
            </Typography>
          ) : (articlesQuery.data || []).length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No articles linked to this concept yet.
            </Typography>
          ) : (
            <div className="space-y-2">
              {(articlesQuery.data || []).map((article) => (
                <div
                  key={article.id}
                  className="rounded border border-beige-200 p-3"
                >
                  <Typography variant="body2" className="font-semibold text-brown-900">
                    {article.title || article.slug || `Article ${article.id.slice(0, 8)}`}
                  </Typography>
                  <Typography variant="caption" className="text-brown-600">
                    {article.slug ? `/content/${article.slug}` : article.id}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Existing outlines
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          {outlinesQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading outlines…
            </Typography>
          ) : outlinesQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {outlinesQuery.error instanceof Error
                ? outlinesQuery.error.message
                : "Unable to load outlines."}
            </Typography>
          ) : (outlinesQuery.data || []).length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No outlines yet for this concept.
            </Typography>
          ) : (
            <div className="space-y-3">
              {(outlinesQuery.data || []).map((outline) => (
                <div
                  key={outline.id}
                  className="rounded border border-beige-200 p-3"
                >
                  <Typography variant="body2" className="font-semibold text-brown-900">
                    {outline.title || outline.name || `Outline ${outline.id.slice(0, 8)}`}
                  </Typography>
                  <Typography variant="caption" className="text-brown-600">
                    Status: {String(outline.status || "unknown")}
                  </Typography>

                  {Array.isArray(outline.bullets) && outline.bullets.length > 0 ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-brown-700">
                      {outline.bullets.slice(0, 8).map((bullet, index) => (
                        <li key={`outline-${outline.id}-bullet-${index}`}>{bullet}</li>
                      ))}
                    </ul>
                  ) : null}

                  {!Array.isArray(outline.bullets) || outline.bullets.length === 0 ? (
                    <Typography variant="body2" className="mt-2 text-brown-700">
                      {(outline.outline || outline.content || "").toString().slice(0, 500) ||
                        "No outline text available yet."}
                    </Typography>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Existing related concepts
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          {relatedQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading existing relationships…
            </Typography>
          ) : relatedQuery.isError ? (
            <Typography variant="body2" className="text-terracotta">
              {relatedQuery.error instanceof Error
                ? relatedQuery.error.message
                : "Unable to load related concepts."}
            </Typography>
          ) : (relatedQuery.data || []).length === 0 ? (
            <Typography variant="body2" className="text-brown-600">
              No relationships yet for this concept.
            </Typography>
          ) : (
            <div className="max-h-64 overflow-auto rounded border border-beige-200">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-cream-50 text-brown-700">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Concept</th>
                    <th className="px-3 py-2 font-semibold">Relationship</th>
                    <th className="px-3 py-2 font-semibold">Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {(relatedQuery.data || []).map((item) => (
                    <tr
                      key={item.relationshipId}
                      className="border-t border-beige-100"
                    >
                      <td className="px-3 py-2 text-brown-900">
                        {item.relatedConceptName || item.relatedConceptId}
                      </td>
                      <td className="px-3 py-2 text-brown-700">
                        {item.relationshipType}
                      </td>
                      <td className="px-3 py-2 text-brown-700">
                        {item.direction}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="w-full" variant="default" elevation={1}>
        <Card.Header divider>
          <Typography variant="h4" className="text-brown-900">
            Generate related concepts
          </Typography>
        </Card.Header>
        <Card.Body className="bg-white">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              id="related-suggestion-limit"
              label="Count"
              type="number"
              min={1}
              max={25}
              value={String(relatedSuggestionLimit)}
              onChange={(event) => {
                const next = Number(event.target.value);
                setRelatedSuggestionLimit(
                  Number.isFinite(next) && next > 0 ? Math.min(25, next) : 1
                );
              }}
              containerClassName="w-28"
            />
            <ActionButton
              size="sm"
              onClick={handleGenerateAndApply}
              isLoading={isGeneratingAndApplying}
              disabled={isGeneratingAndApplying}
            >
              Generate related (auto-apply)
            </ActionButton>
          </div>

          {generationStatus !== "idle" ? (
            <Typography variant="body2" className="mt-2 text-brown-600">
              {generationStatus === "suggesting"
                ? "Generating related concepts…"
                : generationStatus === "applying"
                  ? "Applying generated concepts…"
                  : "Refreshing concept data…"}
            </Typography>
          ) : null}

          {relatedSuggestions.length > 0 ? (
            <div className="mt-3 max-h-64 overflow-auto rounded border border-beige-200">
              <table className="min-w-full text-left text-xs">
                <thead className="bg-cream-50 text-brown-700">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Concept</th>
                    <th className="px-3 py-2 font-semibold">Relationship</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {relatedSuggestions.map((item, index) => {
                    const key = relatedSuggestionKey(item, index);
                    return (
                      <tr key={`related-${key}`} className="border-t border-beige-100">
                        <td className="px-3 py-2 text-brown-900">{item.name}</td>
                        <td className="px-3 py-2 text-brown-700">
                          {item.relationship_type}
                        </td>
                        <td className="px-3 py-2 text-brown-700">{item.concept_type}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
}

export default MedicalConceptDetail;
